// Центральный HTTP-клиент фронтенда.
import axios from 'axios';
import {useAuthStore} from '@/store/authStore.js';
import {REFRESH_TOKEN} from '@/constants';
import {useMaintenanceStore} from '@/modules/maintenance/store/useMaintenanceStore.js'
import notifier from '@/core/utils/notifier.js';
import {formatRateLimitMessage, isRateLimitError} from '@/core/utils/rateLimitHelper.js';
import {getErrorMessage, getErrorType, AUTH_ERRORS, BAN_ERRORS, SILENT_ERRORS} from '@/core/utils/errorMapper.js';

const pendingRequests = new Map();

// Флаг для предотвращения параллельных запросов на refresh
let isRefreshing = false;
// Очередь запросов, ожидающих refresh
let refreshSubscribers = [];

// Момент последнего перевыпуска токена по 403 EMAIL_NOT_CONFIRMED и минимальный
// интервал между такими попытками — см. обработчик 403 ниже.
let lastEmailNotConfirmedRefreshAt = 0;
const EMAIL_NOT_CONFIRMED_REFRESH_COOLDOWN_MS = 30_000;

// Добавляет запрос в очередь ожидания refresh
function subscribeTokenRefresh(onSuccess, onFailure) {
    refreshSubscribers.push({onSuccess, onFailure});
}

// Выполняет все запросы из очереди после успешного refresh.
// Очередь обнуляется до вызова колбэков: иначе повторный запрос, тут же упавший
// в 401, добавился бы в ещё не очищенный массив и был бы выброшен следующим сбросом.
function onRefreshed() {
    const subscribers = refreshSubscribers;
    refreshSubscribers = [];
    subscribers.forEach(({onSuccess}) => onSuccess());
}

// Отклоняет очередь при ошибке refresh. Просто обнулить массив нельзя — промисы
// ожидающих запросов не резолвятся и не реджектятся, и вызывающий код виснет навсегда.
function rejectRefreshQueue(err) {
    const subscribers = refreshSubscribers;
    refreshSubscribers = [];
    subscribers.forEach(({onFailure}) => onFailure?.(err));
}

// Бан — единая проверка: 403 с кодом из BAN_ERRORS может прилететь откуда
// угодно (логин, рефреш, любой защищённый запрос через JWTAuthMiddleware —
// см. Б1-C бэкенда). refreshApi создаётся БЕЗ интерцепторов, поэтому его
// ошибки нужно проверять этой же функцией отдельно в catch-блоках рефреша,
// не полагаясь на то, что они пройдут через основной обработчик.
function isBanResponse(response) {
    const code = response?.data?.error || response?.data?.message;
    return response?.status === 403 && BAN_ERRORS.has(code);
}

async function handleBanResponse(response, navigateTo, routes) {
    const authStore = useAuthStore();
    await authStore.logout();
    await navigateTo(routes.LOGIN);
    const code = response?.data?.error || response?.data?.message;
    notifier.error('axios-ban', getErrorMessage(code, 'Ваш аккаунт заблокирован администрацией.'));
}

// Базовый URL API - вычисляем один раз при создании инстанса
const runtimeOrigin = typeof window !== 'undefined' ? window.location.origin : ''
const envApiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
const hasPlaceholderApi = envApiBaseUrl.includes('example.com')

const API_BASE_URL = runtimeOrigin && (hasPlaceholderApi || !envApiBaseUrl)
    ? `${runtimeOrigin}/api/v1`
    : `${envApiBaseUrl}/api/v1`

// Создаем единый инстанс Axios.
const api = axios.create({
    baseURL: API_BASE_URL,
    // 300с — согласовано с ReadTimeout бэкенда (internal/infra/server/server.go):
    // тот тоже 300с, чтобы успевать читать тело запроса при загрузке фото
    // объявления (до 150МБ = 10 фото по 15МБ) на медленном мобильном
    // соединении. Более короткий таймаут на клиенте обрывал бы аплоад раньше,
    // чем сервер успел бы его принять. Без лимита вовсе — зависшее соединение
    // держит запрос (и часто заблокированную кнопку в UI) бесконечно.
    timeout: 300000,
    headers: {},
});

// Перехватчик запроса: токен, отмена дубликатов, maintenance mode.
api.interceptors.request.use(config => {
        // Формируем уникальный ключ: URL + query params
        const requestKey = config.url + (config.params ? JSON.stringify(config.params) : '');

        // Отменяем предыдущий запрос на этот же URL с теми же параметрами.
        if (pendingRequests.has(requestKey)) {
            pendingRequests.get(requestKey).abort();
        }

        const controller = new AbortController();
        pendingRequests.set(requestKey, controller);
        config.signal = controller.signal;

        const authStore = useAuthStore();
        if (authStore.access_token) {
            config.headers.Authorization = `Bearer ${authStore.access_token}`;
        }

        return config;
    },
    (error) => Promise.reject(error)
);

// Перехватчик ответа: централизованная обработка ошибок и уведомлений.
api.interceptors.response.use(response => {
        const requestKey = response.config.url + (response.config.params ? JSON.stringify(response.config.params) : '');
        pendingRequests.delete(requestKey);

        const maintenanceStore = useMaintenanceStore();
        // Любой успешный ответ сбрасывает флаг maintenance
        if (maintenanceStore.isMaintenance) {
            maintenanceStore.setMaintenance(false)
        }

        // Добавляем status: "success" к успешным ответам для совместимости с фронтендом
        if (response?.data && !response.data.status && !response.data.error) {
            response.data.status = 'success';
        }

        return response
    },
    async error => {
        const requestKey = error.config?.url + (error.config?.params ? JSON.stringify(error.config.params) : '');
        if (requestKey) pendingRequests.delete(requestKey);

        const maintenanceStore = useMaintenanceStore();
        const routerModule = await import("@/router.js");
        const router = routerModule.default;
        const {routes, navigateTo} = routerModule.useNavigation();

        // Обработка ошибок 5xx
        if (error.response?.status && [500, 502, 503, 504].includes(error.response.status)) {
            // Не включаем maintenance mode для эндпоинта отправки тестового сообщения
            const isTestMessageEndpoint = error.config?.url?.includes('/test-message');
            
            if (!maintenanceStore.isMaintenance && !isTestMessageEndpoint) {
                maintenanceStore.setMaintenance(true);
                // Перенаправляем только если мы не на странице Maintenance
                if (router.currentRoute.value.name !== routes.MAINTENANCE.name) {
                    await router.push({name: routes.MAINTENANCE.name})
                }
            }
            return Promise.reject(error);
        }

        // Бан — раньше 401-рефреша и общих 403-хендлеров: разлогин + понятный тост
        // вместо сырого кода или общего "Не удалось обновить токен".
        if (isBanResponse(error.response)) {
            await handleBanResponse(error.response, navigateTo, routes);
            return Promise.reject(error);
        }

        // Обработка 401 с обновлением токена
        if (error.response?.status === 401) {
            // Не пытаемся refresh если это уже запрос на refresh
            if (error.config?.url?.includes('/auth/refresh')) {
                const authStore = useAuthStore();
                await authStore.logout();
                await navigateTo(routes.LOGIN);
                notifier.error('axios-refresh-failed', 'Не удалось обновить токен, выполните вход заново');
                return Promise.reject(error);
            }

            const authStore = useAuthStore();

            // Если уже идёт refresh, добавляем запрос в очередь
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    subscribeTokenRefresh(() => {
                        // Повторяем оригинальный запрос с новым токеном
                        error.config.headers.Authorization = `Bearer ${authStore.access_token}`;
                        resolve(api.request(error.config));
                    }, reject);
                });
            }

            try {
                const refreshToken = localStorage.getItem(REFRESH_TOKEN);
                if (refreshToken) {
                    isRefreshing = true;

                    // Создаём отдельный axios instance без interceptors для refresh запроса
                    const refreshApi = axios.create({
                        baseURL: API_BASE_URL,
                    });

                    const {data} = await refreshApi.post('/auth/refresh', {refresh_token: refreshToken});

                    // Сохраняем новый access токен
                    authStore.setToken(data);

                    // Выполняем все запросы из очереди
                    onRefreshed();

                    // Повторяем оригинальный запрос с новым access токеном
                    error.config.headers.Authorization = `Bearer ${authStore.access_token}`;
                    return api.request(error.config);
                } else {
                    // Если refresh токен отсутствует, перенаправляем на страницу логина
                    await authStore.logout();
                    await navigateTo(routes.LOGIN);
                    notifier.info('axios-refresh-ok', 'Всё хорошо. Токен обновлен');
                }
            } catch (refreshError) {
                rejectRefreshQueue(refreshError);

                // refreshApi без интерцепторов — ban-проверку основного обработчика
                // сюда не долетает, проверяем явно.
                if (isBanResponse(refreshError.response)) {
                    await handleBanResponse(refreshError.response, navigateTo, routes);
                    return Promise.reject(refreshError);
                }

                // 429 на /refresh — не признак невалидной сессии, а исчерпанный бюджет
                // лимитера (например, сосед по общему NAT-IP выжег его неудачными
                // логинами). Разлогинивать легитимного пользователя из-за этого нельзя —
                // сессия остаётся жить, просто просим подождать перед повтором.
                if (refreshError.response?.status === 429) {
                    const retryAfter = refreshError.response?.data?.retry_after;
                    const message = formatRateLimitMessage(refreshError.response?.data ?? {retry_after: retryAfter});
                    notifier.warn('axios-refresh-rate-limited', message, 8000);
                    return Promise.reject(refreshError);
                }

                // Если обновление токена не удалось, разлогиниваем пользователя
                await authStore.logout();
                await navigateTo(routes.LOGIN);
                notifier.error('axios-refresh-failed', 'Не удалось обновить токен, выполните вход заново');
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        const apiErrorMessage = error.response?.data?.error || error.response?.data?.message;

        // Обработка 403 EMAIL_NOT_CONFIRMED — access-токен мог нести устаревший статус:
        // почта подтверждена в другой вкладке, либо токен выпущен до появления клейма
        // status и потому трактуется бэкендом как неподтверждённый (fail-closed).
        // Обновляем токен и повторяем запрос.
        //
        // Два ограничителя. Флаг на конфиге запроса не даёт зациклиться на одном запросе.
        // Кулдаун нужен против другого сценария: у действительно неподтверждённого
        // пользователя каждый клик создаёт новый конфиг, то есть новый refresh, а каждый
        // refresh — это удаление и вставка строки сессии в БД. Без кулдауна настойчивый
        // пользователь выжигает бюджет refreshRateLimit, и 429 прилетает уже легитимному
        // обновлению по 401.
        if (error.response?.status === 403
            && apiErrorMessage === 'EMAIL_NOT_CONFIRMED'
            && !error.config?._emailNotConfirmedRetry
            && Date.now() - lastEmailNotConfirmedRefreshAt > EMAIL_NOT_CONFIRMED_REFRESH_COOLDOWN_MS) {

            error.config._emailNotConfirmedRetry = true;

            const authStore = useAuthStore();
            const refreshToken = localStorage.getItem(REFRESH_TOKEN);

            if (refreshToken) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        subscribeTokenRefresh(() => {
                            error.config.headers.Authorization = `Bearer ${authStore.access_token}`;
                            resolve(api.request(error.config));
                        }, reject);
                    });
                }

                try {
                    isRefreshing = true;
                    lastEmailNotConfirmedRefreshAt = Date.now();

                    const refreshApi = axios.create({baseURL: API_BASE_URL});
                    const {data} = await refreshApi.post('/auth/refresh', {refresh_token: refreshToken});

                    authStore.setToken(data);
                    onRefreshed();

                    error.config.headers.Authorization = `Bearer ${authStore.access_token}`;
                    return api.request(error.config);
                } catch (refreshError) {
                    rejectRefreshQueue(refreshError);

                    // refreshApi без интерцепторов — ban-проверку основного обработчика
                    // сюда не долетает, проверяем явно.
                    if (isBanResponse(refreshError.response)) {
                        await handleBanResponse(refreshError.response, navigateTo, routes);
                        return Promise.reject(refreshError);
                    }

                    // 429 — исчерпан бюджет лимитера, а не невалидная сессия. Сообщаем
                    // время ожидания и не разлогиниваем.
                    if (refreshError.response?.status === 429) {
                        notifier.warn('axios-refresh-rate-limited', formatRateLimitMessage(refreshError.response?.data ?? {}), 8000);
                        return Promise.reject(refreshError);
                    }

                    // Refresh-токен мёртв — сессии больше нет. Молча вернуть исходную 403
                    // нельзя: пользователь останется в приложении с нерабочей сессией и
                    // сообщением про неподтверждённую почту вместо просьбы войти заново.
                    if (refreshError.response?.status === 401) {
                        await authStore.logout();
                        await navigateTo(routes.LOGIN);
                        notifier.error('axios-refresh-failed', 'Не удалось обновить токен, выполните вход заново');
                        return Promise.reject(refreshError);
                    }

                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }
        }

        // Проверка на ошибки авторизации — перенаправляем на логин
        if (AUTH_ERRORS.has(apiErrorMessage)) {
            const authStore = useAuthStore();
            await authStore.logout();
            await navigateTo(routes.LOGIN);
            notifier.warn('axios-auth-error', getErrorMessage(apiErrorMessage, 'Войдите заново'));
            return Promise.reject(error);
        }

        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            return Promise.reject(error);
        }
        // Запрос не получил вообще никакого ответа от сервера — таймаут (axios
        // сам обрывает соединение по достижении config.timeout, error.code
        // ECONNABORTED, error.message вида "timeout of Xms exceeded") или сеть
        // легла совсем (error.message "Network Error"). Ни один из статус-чеков
        // ниже (error.response?.status === ...) не сработает — error.response
        // тут undefined, поэтому без этой ветки сырой английский текст утекал
        // напрямую в UI через error.message в компонентах, которые его читают.
        if (!error.response) {
            const isTimeout = error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '');
            const msg = isTimeout
                ? 'Загрузка занимает слишком много времени — проверьте интернет-соединение и попробуйте снова'
                : 'Не удалось связаться с сервером — проверьте интернет-соединение';
            notifier.error(isTimeout ? 'axios-timeout' : 'axios-no-response', msg);
            console.error('Запрос без ответа сервера:', error.message);
            error.message = msg;
            return Promise.reject(error);
        }

        // Обработка 413 (Request Entity Too Large)
        if (error.response?.status === 413) {
            const msg = getErrorMessage(apiErrorMessage, 'Размер файлов превышает лимит (150MB). Уменьшите размер или загрузите по частям.');
            notifier.error('axios-413', msg);
            console.error('Ошибка 413:', msg);
        }

        // Обработка 400 — показываем только если ошибка не в списке тихих
        if (error.response?.status === 400) {
            const msg = apiErrorMessage || 'Некорректные данные';
            const isTestMessageEndpoint = error.config?.url?.includes('/test-message');

            // Не показываем тост для эндпоинта test-message — там своя обработка в компоненте
            if (!isTestMessageEndpoint && !SILENT_ERRORS.has(msg)) {
                notifier.error('axios-400', getErrorMessage(msg, 'Некорректные данные'));
            }
            console.error('Ошибка 400:', msg);
        }

        // Обработка 403 — Forbidden
        if (error.response?.status === 403) {
            const msg = apiErrorMessage || 'Доступ запрещён';
            if (!SILENT_ERRORS.has(msg)) {
                notifier.error('axios-403', getErrorMessage(msg, 'Доступ запрещён'));
            }
            console.error('Ошибка 403:', msg);
        }

        // Обработка 404 — Not Found
        if (error.response?.status === 404) {
            const msg = apiErrorMessage || 'Не найдено';
            if (!SILENT_ERRORS.has(msg)) {
                notifier.error('axios-404', getErrorMessage(msg, 'Не найдено'));
            }
            console.error('Ошибка 404:', msg);
        }

        // Обработка 409 — Conflict
        if (error.response?.status === 409) {
            const msg = apiErrorMessage || 'Конфликт данных';
            if (!SILENT_ERRORS.has(msg)) {
                notifier.error('axios-409', getErrorMessage(msg, 'Конфликт данных'));
            }
            console.error('Ошибка 409:', msg);
        }

        // Обработка 410 — Gone (код истёк)
        if (error.response?.status === 410) {
            const msg = apiErrorMessage || 'Код истёк';
            notifier.warn('axios-410', getErrorMessage(msg, 'Код истёк'), 5000);
            console.error('Ошибка 410:', msg);
        }

        // Обработка 500 — Internal Server Error (кроме maintenance)
        if (error.response?.status === 500) {
            const msg = apiErrorMessage || 'Внутренняя ошибка сервера';
            notifier.error('axios-500', getErrorMessage(msg, 'Произошла ошибка на сервере. Попробуйте позже.'));
            console.error('Ошибка 500:', msg);
        }

        // Обработка 503 — сервер временно перегружен (например, лимит декодирования изображений)
        if (error.response?.status === 503) {
            const msg = apiErrorMessage || 'Сервер временно перегружен';
            notifier.error('axios-503', getErrorMessage(msg, 'Сервер сейчас перегружен. Попробуйте ещё раз через минуту.'));
            console.error('Ошибка 503:', msg);
        }

        // Обработка 429 (Rate Limit)
        if (isRateLimitError(error)) {
            const rateLimitData = error.response?.data;
            const message = formatRateLimitMessage(rateLimitData);
            notifier.warn('axios-429', message, 8000); // Показываем уведомление на 8 секунд
        }

        // Нормализуем ошибку для совместимости с фронтендом
        if (error.response?.data && !error.response.data.status) {
            error.response.data.status = 'error';
        }

        return Promise.reject(error);
    }
);

export default api;
